import React, { useState, useEffect, useCallback } from 'react';
import { axios } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import toast from 'react-hot-toast';

/* ── SADECE MOBİLDE ÇALIŞAN CSS ── */
const mobileStyles = `
  @media (max-width: 768px) {
    /* Izgara yapılarını mobilde daralt */
    .grid-4 { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
    .grid-2 { grid-template-columns: 1fr !important; gap: 15px !important; }
    
    /* Stat kartlarını küçült */
    .stat-card { padding: 12px !important; }
    .stat-number { font-size: 1.2rem !important; }
    
    /* TABLOYU KART YAPISINA ÇEVİR (Scrollu bitiren kısım) */
    .mobile-to-card thead { display: none; }
    .mobile-to-card, .mobile-to-card tbody, .mobile-to-card tr, .mobile-to-card td { 
      display: block; 
      width: 100%; 
    }
    .mobile-to-card tr { 
      margin-bottom: 15px; 
      border: 1px solid var(--border); 
      border-radius: 12px; 
      background: var(--bg-card);
      overflow: hidden;
    }
    .mobile-to-card td { 
      text-align: right !important; 
      padding: 10px 15px !important; 
      border-bottom: 1px solid var(--border-subtle) !important;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;
    }
    .mobile-to-card td:last-child { border-bottom: none !important; }
    .mobile-to-card td::before {
      content: attr(data-label);
      font-weight: 700;
      color: var(--text-muted);
      text-align: left;
      font-size: 0.75rem;
    }
    
    /* Grafik konteyner yüksekliğini ayarla */
    .chart-box { height: 180px !important; }
  }
`;

function useOgrenciBilgi() {
  const { user } = useAuth();
  const [ogrenci, setOgrenci] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/ogrenciler?limit=10').then(res => {
      if (res.data.success && res.data.data.length > 0) {
        setOgrenci(res.data.data?.[0] || null);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return { ogrenci, loading };
}

// ── Başarı & Denemeler ────────────────────────────────────────
export function VeliBasariPage() {
  const { ogrenci, loading: ogrLoading } = useOgrenciBilgi();
  const [sonuclar, setSonuclar] = useState([]);
  const [analiz, setAnaliz] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ogrenci?.id) return;
    setLoading(true);
    Promise.all([
      axios.get(`/denemeler/ogrenci/${ogrenci.id}`),
      axios.get(`/ogrenciler/${ogrenci.id}/basari-analiz`),
    ]).then(([sRes, aRes]) => {
      if (sRes.data.success) setSonuclar(sRes.data.data.sonuclar || []);
      if (aRes.data.success) setAnaliz(aRes.data.data);
    }).finally(() => setLoading(false));
  }, [ogrenci]);

  if (ogrLoading || loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!ogrenci) return <div className="card"><div className="empty-state"><h3>Öğrenci bilgisi bulunamadı</h3></div></div>;

  const hedefNet = parseFloat(ogrenci.hedef_net || 0);
  const chartData = (analiz?.grafik_data || []).map((d, i) => ({
    name: `D${i + 1}`,
    net: parseFloat(d.toplam_net || 0),
    puan: parseFloat(d.puan || 0),
    hedef: hedefNet,
  }));

  const dersData = (analiz?.ders_netleri || []).map(d => ({
    name: d.ders_adi,
    ort: parseFloat(d.ortalama_net || 0),
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{mobileStyles}</style>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📊 Başarı & Deneme Analizi</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
          {ogrenci.ad} {ogrenci.soyad} için detaylı başarı raporu
        </p>
      </div>

      <div className="grid-4 animate-fadeInUp stagger-1">
        {[
          { label: 'Deneme Sayısı', value: sonuclar.length, color: '#06B6D4', icon: '📝' },
          { label: 'Son Net', value: sonuclar.length ? parseFloat(sonuclar[sonuclar.length - 1].toplam_net).toFixed(1) : '—', color: 'var(--accent)', icon: '📊' },
          { label: 'Hedef Net', value: hedefNet || '—', color: '#3B82F6', icon: '🎯' },
          { label: 'Trend', value: analiz?.trend === 'yukselen' ? '↑ Yükselen' : analiz?.trend === 'dusen' ? '↓ Düşen' : '→ Stabil', color: analiz?.trend === 'yukselen' ? '#10B981' : analiz?.trend === 'dusen' ? '#EF4444' : '#94A3B8', icon: '📈' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ position: 'absolute', top: 0, right: 0, width: 50, height: 50, borderRadius: '0 var(--r-lg) 0 100%', background: s.color, opacity: 0.08 }} />
            <div style={{ fontSize: '1.3rem', marginBottom: 8 }}>{s.icon}</div>
            <div className="stat-number" style={{ color: s.color, fontSize: '1.5rem' }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-2 animate-fadeInUp stagger-2">
        <div className="card">
          <div className="section-title" style={{ marginBottom: 16 }}>📈 Net Trendi</div>
          <div className="chart-box" style={{ height: 200 }}>
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="veliBasariGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EC4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }} />
                  <Area type="monotone" dataKey="net" stroke="#EC4899" strokeWidth={2.5} fill="url(#veliBasariGrad)" name="Net" dot={{ fill: '#EC4899', r: 3 }} />
                  {hedefNet > 0 && <Area type="monotone" dataKey="hedef" stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="4 4" fill="none" name="Hedef" dot={false} />}
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Yeterli deneme yok</p></div>}
          </div>
        </div>

        <div className="card">
          <div className="section-title" style={{ marginBottom: 16 }}>📚 Ders Bazlı Ortalama</div>
          <div className="chart-box" style={{ height: 200 }}>
            {dersData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dersData} barSize={22}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fill: '#4B5563', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }} />
                  <Bar dataKey="ort" name="Ort. Net" fill="#EC4899" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Veri yok</p></div>}
          </div>
        </div>
      </div>

      <div className="card animate-fadeInUp stagger-3">
        <div className="section-title" style={{ marginBottom: 16 }}>📋 Tüm Deneme Sonuçları</div>
        <div className="table-container">
          <table className="table mobile-to-card">
            <thead><tr><th>Deneme</th><th>Tarih</th><th>Toplam Net</th><th>Puan</th><th>Değişim</th></tr></thead>
            <tbody>
              {[...sonuclar].reverse().map((s, i, arr) => {
                const net = parseFloat(s.toplam_net || 0);
                const oncekiNet = i < arr.length - 1 ? parseFloat(arr[i + 1].toplam_net || 0) : null;
                const degisim = oncekiNet !== null ? net - oncekiNet : null;
                return (
                  <tr key={i}>
                    <td data-label="Deneme" style={{ fontWeight: 600 }}>{s.deneme_adi}</td>
                    <td data-label="Tarih" style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{s.tarih ? new Date(s.tarih).toLocaleDateString('tr-TR') : '—'}</td>
                    <td data-label="Toplam Net"><span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--accent)', fontSize: '1.05rem' }}>{parseFloat(net||0).toFixed(1)}</span></td>
                    <td data-label="Puan" style={{ color: '#3B82F6', fontWeight: 600 }}>{parseFloat(s.puan || 0).toFixed(0)}</td>
                    <td data-label="Değişim">
                      {degisim === null ? <span className="badge badge-muted">İlk</span> :
                        degisim > 0 ? <span className="badge badge-success">↑ +{parseFloat(degisim||0).toFixed(1)}</span> :
                        degisim < 0 ? <span className="badge badge-danger">↓ {parseFloat(degisim||0).toFixed(1)}</span> :
                        <span className="badge badge-muted">→ Aynı</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Devam Durumu ──────────────────────────────────────────────
export function VeliDevamsizlikPage() {
  const { ogrenci, loading: ogrLoading } = useOgrenciBilgi();
  const [yoklama, setYoklama] = useState([]);
  const [ozet, setOzet] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ogrenci?.id) return;
    setLoading(true);
    axios.get(`/yoklama/ogrenci/${ogrenci.id}`).then(res => {
      if (res.data.success) {
        setYoklama(res.data.data.kayitlar || []);
        setOzet(res.data.data.ozet || {});
      }
    }).finally(() => setLoading(false));
  }, [ogrenci]);

  if (ogrLoading || loading) return <div className="page-loader"><div className="spinner" /></div>;

  const DURUM_COLORS = { mevcut: '#10B981', devamsiz: '#EF4444', gec: '#F97316', izinli: '#3B82F6' };
  const DURUM_ICONS = { mevcut: '✅', devamsiz: '❌', gec: '⏰', izinli: '📋' };
  const katilimOrani = ozet.toplam > 0 ? ((ozet.mevcut || 0) / ozet.toplam * 100).toFixed(0) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{mobileStyles}</style>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>✅ Devam Durumu</h2>
      </div>

      <div className="grid-4 animate-fadeInUp stagger-1">
        {[
          { label: 'Toplam Ders', value: ozet.toplam || 0, color: '#3B82F6', icon: '📚' },
          { label: 'Mevcut', value: ozet.mevcut || 0, color: '#10B981', icon: '✅' },
          { label: 'Devamsız', value: ozet.devamsiz || 0, color: '#EF4444', icon: '❌' },
          { label: 'Katılım', value: `%${katilimOrani}`, color: parseInt(katilimOrani) >= 85 ? '#10B981' : '#F97316', icon: '📊' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ fontSize: '1.3rem', marginBottom: 8 }}>{s.icon}</div>
            <div className="stat-number" style={{ color: s.color, fontSize: '1.5rem' }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card animate-fadeInUp stagger-3">
        <div className="section-title" style={{ marginBottom: 16 }}>📋 Yoklama Geçmişi</div>
        <div className="table-container">
          <table className="table mobile-to-card">
            <thead><tr><th>Tarih</th><th>Ders</th><th>Sınıf</th><th>Durum</th></tr></thead>
            <tbody>
              {[...yoklama].sort((a, b) => new Date(b.tarih) - new Date(a.tarih)).slice(0, 20).map((y, i) => (
                <tr key={i}>
                  <td data-label="Tarih" style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {new Date(y.tarih).toLocaleDateString('tr-TR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                  </td>
                  <td data-label="Ders">{y.ders_adi}</td>
                  <td data-label="Sınıf" style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{y.sinif_adi}</td>
                  <td data-label="Durum">
                    <span style={{
                      background: (DURUM_COLORS[y.durum] || '#94A3B8') + '20',
                      color: DURUM_COLORS[y.durum] || '#94A3B8',
                      padding: '3px 10px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600,
                    }}>
                      {DURUM_ICONS[y.durum]} {y.durum}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}